import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/context/LanguageContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import TermsOfUse from "./pages/TermsOfUse";
import Privacy from "./pages/Privacy";
import SignIn from "./pages/Auth/SignIn";
import SignUp from "./pages/Auth/SignUp";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import History from "./pages/History/HistoryPageContainer";
import Explore from "./pages/Explore/ExplorePageContainer";
import Pricing from "./pages/Pricing";
import { RoomPage } from "./pages/dashboard/RoomPage";
import { RoomExamPage } from "./pages/dashboard/RoomExamPage";
import { RoomExamSummaryPage } from "./pages/dashboard/RoomExamSummaryPage";
import ChatPage from "./pages/chat/ChatPage";
import ContentPage from "./pages/Content/ContentPage";
import ReportsPageContainer from "./pages/Reports/ReportsPageContainer";
import CoursePage from "./pages/Course/CoursePage";
import CourseCreatingPage from "./pages/Course/CourseCreatingPage";
import LessonPage from "./pages/Course/LessonPage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import PublicRoomPage from "./pages/Public/PublicRoomPage";
import InviteRedirect from "./pages/InviteRedirect";
import TeamInviteAccept from "./pages/TeamInviteAccept";
import AITutorPage from "./pages/features/AITutorPage";
import AIFlashcardsPage from "./pages/features/AIFlashcardsPage";
import AIQuizGeneratorPage from "./pages/features/AIQuizGeneratorPage";
import AIExamGeneratorPage from "./pages/features/AIExamGeneratorPage";
import AILectureSummarizerPage from "./pages/features/AILectureSummarizerPage";
import WhatIsAITutorPage from "./pages/geo/WhatIsAITutorPage";
import HowAIFlashcardsWorkPage from "./pages/geo/HowAIFlashcardsWorkPage";
import BestAIStudyToolsPage from "./pages/geo/BestAIStudyToolsPage";
import BlogListPage from "./pages/Blog/BlogListPage";
import BlogPostPage from "./pages/Blog/BlogPostPage";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="shattara-theme">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/termsofuse" element={<TermsOfUse />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/auth/signin" element={<SignIn />} />
                <Route path="/auth/signup" element={<SignUp />} />
                <Route path="/auth/forgotpassword" element={<ForgotPassword />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/history" element={<History />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/reports" element={<ReportsPageContainer />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/:chatId" element={<ChatPage />} />
                <Route path="/room/:roomId" element={<RoomPage />} />
                <Route path="/r/:slug" element={<PublicRoomPage />} />
                <Route path="/room/:roomId/exam/:examId" element={<RoomExamPage />} />
                <Route path="/room/:roomId/exam/:examId/summary" element={<RoomExamSummaryPage />} />
                <Route path="/content/:contentId" element={<ContentPage />} />
                <Route path="/course/creating/:requestId" element={<CourseCreatingPage />} />
                <Route path="/course/:courseId" element={<CoursePage />} />
                <Route path="/course/:courseId/lesson/:nodeId" element={<LessonPage />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/invite/:code" element={<InviteRedirect />} />
                <Route path="/team/invite/:token" element={<TeamInviteAccept />} />
                <Route path="/ai-tutor" element={<AITutorPage />} />
                <Route path="/ai-flashcards" element={<AIFlashcardsPage />} />
                <Route path="/ai-quiz-generator" element={<AIQuizGeneratorPage />} />
                <Route path="/ai-exam-generator" element={<AIExamGeneratorPage />} />
                <Route path="/ai-lecture-summarizer" element={<AILectureSummarizerPage />} />
                <Route path="/what-is-ai-tutor" element={<WhatIsAITutorPage />} />
                <Route path="/how-ai-flashcards-work" element={<HowAIFlashcardsWorkPage />} />
                <Route path="/best-ai-study-tools" element={<BestAIStudyToolsPage />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/terms" element={<Navigate to="/termsofuse" replace />} />
                <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />
                <Route path="/team" element={<Navigate to="/" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
